import { useCallback, useState } from 'react';

export const CHAT_ZOOM_STEPS = [0.8, 0.9, 1, 1.1, 1.25, 1.5];
const STORAGE_KEY = 'chatZoom';

const readStoredZoom = () => {
  try {
    const value = parseFloat(localStorage.getItem(STORAGE_KEY));
    return CHAT_ZOOM_STEPS.includes(value) ? value : 1;
  } catch {
    return 1;
  }
};

export const isChatZoomSupported = () => {
  try {
    return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1.1');
  } catch {
    return false;
  }
};

export const useChatZoom = (scrollRef) => {
  const [zoom, setZoom] = useState(readStoredZoom);

  const applyZoom = useCallback((next) => {
    const el = scrollRef?.current;
    const prev = zoom;
    const wasAtBottom = !!el && el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    const prevTop = el ? el.scrollTop : 0;
    setZoom(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {}
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = wasAtBottom ? el.scrollHeight : prevTop * (next / prev);
      });
    }
  }, [scrollRef, zoom]);

  const index = CHAT_ZOOM_STEPS.indexOf(zoom);
  const canZoomIn = index < CHAT_ZOOM_STEPS.length - 1;
  const canZoomOut = index > 0;

  const zoomIn = useCallback(() => {
    if (canZoomIn) applyZoom(CHAT_ZOOM_STEPS[index + 1]);
  }, [applyZoom, canZoomIn, index]);

  const zoomOut = useCallback(() => {
    if (canZoomOut) applyZoom(CHAT_ZOOM_STEPS[index - 1]);
  }, [applyZoom, canZoomOut, index]);

  const resetZoom = useCallback(() => {
    if (zoom !== 1) applyZoom(1);
  }, [applyZoom, zoom]);

  return { zoom, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut };
};
